import { useCallback, useMemo } from 'react';
import cn from 'bem-cn';
import { FeedSortType } from '@ai-platform/utils';
import { useSearchParams } from 'react-router-dom';

import { feedActions, useActions, useFeedSelector } from '../../../store';
import { Dropdown, Stack } from '../../UI';
import { DropdownItem } from '../../UI/Dropdown/Dropdown.types';

import './FeedSort.scss';
import { SEARCH_PARAMS } from '../../../constants';
import { useSyncSortFromUrl } from './FeedSort.hooks';
import { FeedSortProps } from './FeedSort.types';

const b = cn('feed-sort');

const sortCommonItems = [
    { id: FeedSortType.All, value: FeedSortType.All, label: 'Всё' },
    { id: FeedSortType.Newest, value: FeedSortType.Newest, label: 'Новое' },
];

const bestSortItems = [
    { id: FeedSortType.BestOfAll, value: FeedSortType.BestOfAll, label: 'Всё время' },
    { id: FeedSortType.BestOfWeek, value: FeedSortType.BestOfWeek, label: 'Неделя' },
    { id: FeedSortType.BestOfMonth, value: FeedSortType.BestOfMonth, label: 'Месяц' },
];

export const FeedSort = (props: FeedSortProps) => {
    const { anchorRef } = props;

    const { sort } = useFeedSelector();
    const { setSort } = useActions(feedActions);
    const [, setSearchParams] = useSearchParams();

    useSyncSortFromUrl();

    const isBestSortSelected = bestSortItems.some(item => item.value === sort);
    const selectedBestSortValues = useMemo(() => [sort], [sort]);

    const handleChangeSort = useCallback(
        (type: FeedSortType) => {
            if (type === sort) {
                return;
            }

            // Обновляем стор
            setSort(type);

            // Обновляем URL
            setSearchParams(
                params => {
                    if (type === FeedSortType.All) {
                        params.delete(SEARCH_PARAMS.SORT);
                    } else {
                        params.set(SEARCH_PARAMS.SORT, type);
                    }

                    return params;
                },
                { replace: true },
            );

            requestAnimationFrame(() => {
                if (anchorRef.current) {
                    const rect = anchorRef.current?.getBoundingClientRect();
                    const newTopPosition = window.scrollY + rect.top;
                    const scrollDifference = Math.abs(newTopPosition - window.scrollY);

                    // Скроллим только если разница больше 300px
                    if (scrollDifference > 300) {
                        window.scrollTo({ top: newTopPosition, behavior: 'smooth' });
                    }
                }
            });
        },
        [anchorRef, setSort, sort, setSearchParams],
    );

    const handleTypeSelect = (type: FeedSortType) => {
        return () => handleChangeSort(type);
    };

    const handleBestSortSelect = useCallback(
        (item: DropdownItem<FeedSortType>) => {
            handleChangeSort(item.value);
        },
        [handleChangeSort],
    );

    return (
        <Stack direction="row" className={b()}>
            {sortCommonItems.map(item => (
                <button
                    key={item.id}
                    className={b('item', { active: sort === item.value })}
                    onClick={handleTypeSelect(item.value)}
                    disabled={sort === item.value}
                >
                    {item.label}
                </button>
            ))}
            <Dropdown
                triggerClassName={b('dropdown-trigger', { active: isBestSortSelected })}
                placement="bottom-end"
                trigger="Лучшее"
                items={bestSortItems}
                selectedValues={selectedBestSortValues}
                onSelect={handleBestSortSelect}
                autoCloseOnTriggerHidden
            />
        </Stack>
    );
};
